import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import { useGoBack } from "../../../hooks/useGoBack";

import type { RootState } from "../../../store/store";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import {
  deactivateStoreByIdApi,
  getStoresPaginatedApi,
  reactivateStoreByIdApi,
  StorePublic,
} from "../storeApi";
import StoreFormPanel from "../components/StoreFormPanel";
import { STORE_DETAIL_PAGE } from "../routes";

import "./StorePage.css";

type StatusFilter = "active" | "deleted";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Ativas" },
  { value: "deleted", label: "Desativadas" },
];

/**
 * Lojas — lista com busca e status. A linha abre a ficha da loja
 * (`/lojas/:id`, desde 22/10/2026), onde ficam os dados, o horário, a posição, a
 * equipe e o quiosque; o painel lateral ficou só para a loja nova.
 *
 * Substitui o hub antigo de 2 cards (Cadastrar / Gerenciar), que abria os
 * modais `CreateStoreModal` e `UpdateStoreModal`; a lógica deles (validação,
 * mensagens, regra de "loja desativada não se edita") veio para cá e para o
 * `StoreFormPanel`. O filtro "Todas" e o filtro por UF do design ficaram de
 * fora: `GET /stores` não devolve o status de cada loja nem filtra por UF.
 */
const StorePage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [status, setStatus] = useState<StatusFilter>("active");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<StorePublic[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [creating, setCreating] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<StorePublic | null>(null);
  const [toggling, setToggling] = useState(false);


  // Busca com espera curta: digitar não dispara uma requisição por tecla.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (!token) return;
    let active = true;

    setLoading(true);
    getStoresPaginatedApi(token, {
      page,
      limit: PAGE_SIZE,
      status,
      ...(search ? { nome: search } : {}),
    })
      .then((data) => {
        if (!active) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch(() => active && toast.error("Erro ao buscar lojas."))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [token, page, status, search, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleStatusChange = (next: StatusFilter) => {
    setStatus(next);
    setPage(1);
  };

  // Loja nova abre a ficha, para seguir com o horário, a posição e a equipe.
  const handleCreated = (saved: StorePublic) => {
    setCreating(false);
    navigate(STORE_DETAIL_PAGE(saved.id));
  };

  const openStore = (store: StorePublic) => navigate(STORE_DETAIL_PAGE(store.id));

  const handleConfirmToggle = async () => {
    if (!toggleTarget || toggling) return;
    try {
      setToggling(true);
      if (status === "active") {
        await deactivateStoreByIdApi(toggleTarget.id);
        toast.success("Loja desativada com sucesso!");
      } else {
        await reactivateStoreByIdApi(toggleTarget.id);
        toast.success("Loja reativada com sucesso!");
      }
      setToggleTarget(null);
      // A loja sai do filtro atual; se a página ficou vazia, volta uma.
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
      else reload();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : status === "active"
            ? "Erro ao desativar loja."
            : "Erro ao reativar loja.",
      );
    } finally {
      setToggling(false);
    }
  };

  const countLabel = `${total} ${total === 1 ? "loja" : "lojas"} ${
    status === "active" ? (total === 1 ? "ativa" : "ativas") : total === 1 ? "desativada" : "desativadas"
  }`;
  const isDeactivatedList = status === "deleted";

  const emptyMessage = search
    ? `Nenhuma loja encontrada para "${search}".`
    : isDeactivatedList
      ? "Nenhuma loja desativada."
      : "Nenhuma loja cadastrada.";

  return (
    <div className="ecoStores">
      <main className="ecoStores__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Lojas" },
          ]}
          title="Lojas"
          subtitle="Cadastro das lojas atendidas. Abra a ficha da loja para os dados, o horário, a posição, a equipe e o quiosque."
          onBack={goBack}
          backLabel="Voltar para Operações"
          actions={
            <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setCreating(true)}>
              <EcoIcon name="plus" size={18} />
              Nova loja
            </button>
          }
        />

        <section className="ecoCard ecoStores__card" aria-label="Lista de lojas">
          <div className="ecoStores__toolbar">
            <div className="ecoStores__filters">
              <label htmlFor="store-busca" className="ecoVisuallyHidden">
                Buscar por nome fantasia
              </label>
              <div className="ecoSearch ecoStores__search">
                <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                <input
                  id="store-busca"
                  type="search"
                  className="ecoInput ecoInput--sm"
                  placeholder="Buscar por nome fantasia"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <EcoSegmented
                label="Status"
                options={STATUS_OPTIONS}
                value={status}
                onChange={handleStatusChange}
                className="ecoStores__status"
              />
            </div>
            <span className="ecoStores__count" aria-live="polite">
              {loading ? "Carregando..." : countLabel}
            </span>
          </div>

          {!loading && items.length === 0 ? (
            <div className="ecoStores__empty">
              <p className="ecoEmpty">{emptyMessage}</p>
            </div>
          ) : (
            <>
              {/* Desktop: tabela */}
              <div className="ecoTableWrap ecoStores__table">
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th scope="col">Loja</th>
                      <th scope="col">Endereço</th>
                      <th scope="col">UF</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="ecoTable__num">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading}>
                    {items.map((store) => (
                      <tr key={store.id}>
                        <td className="ecoStores__name">{store.nomeFantasia}</td>
                        <td className={store.endereco ? "ecoTable__muted" : "ecoTable__faint"}>
                          {store.endereco || "Sem endereço cadastrado"}
                        </td>
                        <td className="ecoTable__muted">{store.estado || "—"}</td>
                        <td>
                          <span className={`ecoPill${isDeactivatedList ? " ecoPill--off" : ""}`}>
                            {isDeactivatedList ? "Desativada" : "Ativa"}
                          </span>
                        </td>
                        <td className="ecoTable__num">
                          <div className="ecoStores__actions">
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm"
                              onClick={() => openStore(store)}
                              aria-label={`Abrir a ficha de ${store.nomeFantasia}`}
                            >
                              Abrir ficha
                            </button>
                            <button
                              type="button"
                              className={`ecoBtn ecoBtn--sm${isDeactivatedList ? "" : " ecoBtn--danger"}`}
                              onClick={() => setToggleTarget(store)}
                              aria-label={`${isDeactivatedList ? "Reativar" : "Desativar"} ${store.nomeFantasia}`}
                            >
                              {isDeactivatedList ? "Reativar" : "Desativar"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Celular: lista */}
              <ul className="ecoStores__list" aria-busy={loading}>
                {items.map((store) => (
                  <li key={store.id}>
                    <button type="button" className="ecoStores__item" onClick={() => openStore(store)}>
                      <span className="ecoStores__uf" aria-hidden="true">
                        {store.estado || "—"}
                      </span>
                      <span className="ecoStores__itemText">
                        <span className="ecoStores__itemName">{store.nomeFantasia}</span>
                        <span
                          className={`ecoStores__itemAddr${store.endereco ? "" : " ecoStores__itemAddr--empty"}`}
                        >
                          {store.endereco || "Sem endereço cadastrado"}
                        </span>
                      </span>
                      <EcoIcon name="chevronRight" className="ecoStores__chevron" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="ecoStores__pagerWrap">
            <EcoPagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} disabled={loading} />
          </div>
        </section>
      </main>

      {creating ? <StoreFormPanel onClose={() => setCreating(false)} onSaved={handleCreated} /> : null}

      <EcoConfirmDialog
        open={Boolean(toggleTarget)}
        title={isDeactivatedList ? "Reativar loja?" : "Desativar loja?"}
        message={
          toggleTarget ? (
            isDeactivatedList ? (
              <>
                <strong>{toggleTarget.nomeFantasia}</strong> volta a aparecer nas escalas e avaliações.
              </>
            ) : (
              <>
                <strong>{toggleTarget.nomeFantasia}</strong> deixa de aparecer nas escalas e
                avaliações. Dá para reativar depois.
              </>
            )
          ) : null
        }
        confirmLabel={isDeactivatedList ? "Reativar" : "Desativar"}
        tone={isDeactivatedList ? "default" : "danger"}
        busy={toggling}
        onConfirm={handleConfirmToggle}
        onCancel={() => setToggleTarget(null)}
      />
    </div>
  );
};

export default StorePage;
